import { formatDisplayEmail } from '@bs/core'
import { Button, Modal, pantryColors } from '@pantry/ui'
import {
  Ban,
  Clock,
  Eye,
  Flag,
  Link as LinkIcon,
  Mail,
  MousePointerClick,
  Send,
  ShoppingBag,
  ShoppingCart,
  Square,
  User,
} from 'lucide-react'
import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type {
  CartDetail,
  CartEmailRecord,
  CartOutcome,
  CartRow,
  CartSnapshot,
  CartTimelineType,
  CartUpNext,
} from '../../../../types/cartRecovery'
import { ProductThumb } from '../../../Customer/components/profile/tabs/behavior/v2/panels/products/ProductThumb'
import { TIER_LABEL, TIER_PALETTE } from '../../utils/tier'
import type { LoyaltyTier } from '../../../../types/loyalty'
import { T } from '../builder/builderStyles'
import { getMarketingErrorMessage } from '../hooks/useMarketingToast'
import {
  OUTCOME_META,
  fmtDateTime,
  fmtDelay,
  fmtDuration,
  fmtIn,
  fmtMoney,
} from './cartRecoveryFormat'
import {
  useSendNextCartRecoveryEmail,
  useStopCartRecoveryCart,
} from './useCartRecovery'

const p = pantryColors

export const SectionTitle: React.FC<{
  children: React.ReactNode
  right?: React.ReactNode
}> = ({ children, right }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: 10,
      margin: '18px 0 10px',
    }}
  >
    <span
      style={{
        fontSize: 11.5,
        fontWeight: 700,
        color: p.gray[500],
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {children}
    </span>
    {right && (
      <span style={{ fontSize: 11.5, color: p.gray[400], textAlign: 'right' }}>
        {right}
      </span>
    )}
  </div>
)

export const OutcomePill: React.FC<{ outcome: CartOutcome }> = ({
  outcome,
}) => {
  const meta = OUTCOME_META[outcome] ?? OUTCOME_META.skipped
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 9px',
        borderRadius: 20,
        fontSize: 11.5,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        color: meta.fg,
        background: meta.bg,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: 'currentColor',
        }}
      />
      {meta.label}
    </span>
  )
}

const OUTCOME_ICON: Record<CartOutcome, React.ReactNode> = {
  recovered: <ShoppingBag size={17} />,
  in_progress: <Clock size={17} />,
  queued: <Clock size={17} />,
  stopped: <Square size={17} />,
  lost: <Flag size={17} />,
  skipped: <Ban size={17} />,
}

const outcomeDetail = (
  cart: CartRow,
  emails: CartEmailRecord[],
): { title: string; text: string } => {
  const total = cart.total_emails
  switch (cart.outcome) {
    case 'recovered': {
      const order = cart.order
      const via = order?.via_email
      const email = emails.find((e) => e.number === via)
      const after =
        order?.at && cart.abandoned_at
          ? `${fmtDuration(new Date(order.at).getTime() - new Date(cart.abandoned_at).getTime())} after abandoning`
          : ''
      return {
        title: 'Recovered',
        text: [
          via ? `Email ${via}` : '',
          after,
          email?.coupon ? `email included ${email.coupon}` : '',
        ]
          .filter(Boolean)
          .join(' · '),
      }
    }
    case 'stopped':
      return {
        title: 'Reminders stopped',
        text: `${cart.reason_label || 'Stopped'}. No more emails will be sent for this cart.`,
      }
    case 'lost':
      return {
        title: 'Lost',
        text: `All ${total} emails sent with no order. ${fmtMoney(cart.value)} not recovered.`,
      }
    case 'skipped':
      return {
        title: 'Skipped, no reminders sent',
        text: cart.reason_label || 'This cart did not get a reminder.',
      }
    case 'queued':
      return {
        title: 'Queued for Email 1',
        text: cart.next_send_at
          ? `Sends ${fmtDateTime(cart.next_send_at)} (in ${fmtIn(cart.next_send_at)}), unless they come back first.`
          : 'Sends soon, unless they come back first.',
      }
    default:
      return {
        title: 'In progress',
        text: cart.held
          ? 'Automation is paused. Emails continue when it is turned back on.'
          : cart.next_send_at
            ? `Email ${cart.next_email} sends ${fmtDateTime(cart.next_send_at)} (in ${fmtIn(cart.next_send_at)})`
            : `All ${total} emails sent. Waiting to see if they order.`,
      }
  }
}

export const OutcomeBox: React.FC<{
  cart: CartRow
  emails: CartEmailRecord[]
}> = ({ cart, emails }) => {
  const meta = OUTCOME_META[cart.outcome] ?? OUTCOME_META.skipped
  const { title, text } = outcomeDetail(cart, emails)
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '13px 15px',
        borderRadius: 10,
        border: `1px solid ${meta.fg}33`,
        background: meta.bg,
      }}
    >
      <span style={{ color: p.gray[700], display: 'flex' }}>
        {OUTCOME_ICON[cart.outcome]}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: p.gray[800] }}>
          {title}
        </div>
        <div style={{ fontSize: 12.5, color: p.gray[600] }}>{text}</div>
      </div>
      {cart.order && (
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: T.green }}>
            {fmtMoney(cart.order.amount)}
          </div>
          {cart.order.number && (
            <div style={{ fontSize: 11.5, color: p.gray[500] }}>
              Order #{cart.order.number}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const initials = (label: string): string =>
  label
    .split(/\s+/)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

const customerPath = (storeId: string | undefined, customerId: string) =>
  `/s/${storeId}/customers/${customerId}/view`

export const CustomerCard: React.FC<{
  customer: {
    customer_id: string
    email: string
    name?: string
    phone?: string
    tier?: string
    subscribed?: boolean
  }
}> = ({ customer }) => {
  const { storeId } = useParams<{ storeId: string }>()
  const navigate = useNavigate()
  const email = formatDisplayEmail(customer.email)
  const label = customer.name || email || customer.customer_id
  const palette = TIER_PALETTE[customer.tier as LoyaltyTier]
  return (
    <>
      <SectionTitle
        right={
          customer.customer_id ? (
            <button
              type="button"
              onClick={() =>
                navigate(customerPath(storeId, customer.customer_id))
              }
              style={{
                border: 'none',
                background: 'none',
                padding: 0,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                color: p.brand.primary,
              }}
            >
              View profile →
            </button>
          ) : undefined
        }
      >
        Customer
      </SectionTitle>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 14px',
          border: `1px solid ${p.gray[200]}`,
          borderRadius: 10,
        }}
      >
        <span
          style={{
            width: 38,
            height: 38,
            borderRadius: '50%',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            fontWeight: 700,
            color: p.white,
            background: p.brand.primary,
          }}
        >
          {initials(label)}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: p.gray[800] }}>
            {label}
          </div>
          {email && email !== label && (
            <div style={{ fontSize: 12, color: p.gray[500] }}>{email}</div>
          )}
          {customer.phone && (
            <div style={{ fontSize: 12, color: p.gray[500] }}>
              {customer.phone}
            </div>
          )}
        </div>
        {customer.subscribed === false && customer.email && (
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              padding: '1px 7px',
              borderRadius: 10,
              background: p.red[50],
              color: p.red[700],
            }}
          >
            Unsubscribed
          </span>
        )}
        {palette && (
          <span
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              padding: '2px 9px',
              borderRadius: 20,
              color: palette.fg,
              background: palette.bg,
            }}
          >
            {TIER_LABEL[customer.tier as LoyaltyTier]}
          </span>
        )}
      </div>
    </>
  )
}

const TotalRow: React.FC<{
  label: string
  value: string
  bold?: boolean
}> = ({ label, value, bold }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 12,
      padding: '5px 0',
      fontSize: 13,
      fontWeight: bold ? 700 : 400,
      color: bold ? p.gray[800] : p.gray[600],
    }}
  >
    <span>{label}</span>
    <span>{value}</span>
  </div>
)

const SnapshotLines: React.FC<{ cart: CartSnapshot }> = ({ cart }) => (
  <>
    {cart.items.map((item, i) => (
      <div
        key={`${item.name}-${item.variant}-${i}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '9px 0',
          borderBottom: `1px solid ${p.gray[100]}`,
        }}
      >
        <ProductThumb src={item.image} name={item.name} size={38} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: p.gray[800] }}>
            {item.name}
          </div>
          {item.variant && (
            <div style={{ fontSize: 12, color: p.gray[500] }}>
              {item.variant}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: p.gray[800] }}>
            {fmtMoney(item.line_total)}
          </div>
          <div style={{ fontSize: 11.5, color: p.gray[400] }}>
            {item.qty} × {fmtMoney(item.unit_price)}
          </div>
        </div>
      </div>
    ))}
  </>
)

const NotCaptured: React.FC = () => (
  <div style={{ fontSize: 13, color: p.gray[500], padding: '8px 0' }}>
    Cart not captured. This email was sent before carts were saved.
  </div>
)

const CouponRow: React.FC<{ email: CartEmailRecord }> = ({ email }) =>
  email.coupon ? (
    <TotalRow label={`Coupon in Email ${email.number}`} value={email.coupon} />
  ) : null

const itemsLabel = (count: number) => `${count} item${count === 1 ? '' : 's'}`

// The cart and coupon in the latest email sent, plus the order total once it's
// recovered. `fallback` renders when no email was captured.
export const LatestEmailCart: React.FC<{
  email: CartEmailRecord | null | undefined
  order?: CartRow['order']
  fallback?: React.ReactNode
}> = ({ email, order, fallback }) => {
  const cart = email?.cart
  return (
    <>
      <SectionTitle
        right={
          email
            ? `In Email ${email.number} · ${fmtDateTime(email.sent_at)}`
            : undefined
        }
      >
        Cart{cart ? ` · ${itemsLabel(cart.count)}` : ''}
      </SectionTitle>
      {email && !cart && <NotCaptured />}
      {cart && <SnapshotLines cart={cart} />}
      {cart && (
        <div style={{ marginTop: 6 }}>
          <TotalRow label="Subtotal" value={fmtMoney(cart.subtotal)} />
        </div>
      )}
      {email && <CouponRow email={email} />}
      {order && (
        <TotalRow label="Order total" value={fmtMoney(order.amount)} bold />
      )}
      {!cart && fallback}
    </>
  )
}

const EmailCartAtSend: React.FC<{ email: CartEmailRecord }> = ({ email }) => (
  <div
    style={{
      margin: '8px 0 2px',
      padding: '4px 12px 8px',
      border: `1px solid ${p.gray[200]}`,
      borderRadius: 8,
      background: p.gray[50],
    }}
  >
    <div
      style={{
        fontSize: 11.5,
        color: p.gray[500],
        padding: '6px 0 2px',
      }}
    >
      Cart when Email {email.number} was sent
    </div>
    {email.cart ? (
      <>
        <SnapshotLines cart={email.cart} />
        <div style={{ marginTop: 4 }}>
          <TotalRow label="Subtotal" value={fmtMoney(email.cart.subtotal)} />
        </div>
      </>
    ) : (
      <NotCaptured />
    )}
    <CouponRow email={email} />
  </div>
)

export type TimelineKind = CartTimelineType | 'skipped' | 'failed' | 'scheduled'

export interface TimelineRow {
  key: string
  kind: TimelineKind
  at?: string | null
  text: string
  sub?: string
  // A sent row's email; a "View cart" button shows it unless it's the latest.
  email?: CartEmailRecord
}

const TIMELINE_ICON: Record<
  TimelineKind,
  { icon: React.ReactNode; fg: string; bg: string }
> = {
  abandoned: { icon: <ShoppingCart size={14} />, fg: T.gray500, bg: T.white },
  sent: { icon: <Mail size={14} />, fg: T.blue, bg: T.blueBg },
  open: { icon: <Eye size={14} />, fg: T.blue, bg: T.blueBg },
  click: {
    icon: <MousePointerClick size={14} />,
    fg: T.orange,
    bg: T.orangeBg,
  },
  order: { icon: <ShoppingBag size={14} />, fg: T.green, bg: T.greenLight },
  stopped: { icon: <Square size={14} />, fg: T.amber, bg: T.amberBg },
  lost: { icon: <Flag size={14} />, fg: T.gray500, bg: T.white },
  skipped: { icon: <Ban size={14} />, fg: T.amber, bg: T.amberBg },
  failed: { icon: <Ban size={14} />, fg: T.red, bg: T.redBg },
  scheduled: { icon: <Clock size={14} />, fg: T.gray500, bg: T.white },
}

// Newest first, with each older email's cart one click away.
export const CartTimeline: React.FC<{
  rows: TimelineRow[]
  latestEmail?: CartEmailRecord | null
}> = ({ rows, latestEmail }) => {
  const [open, setOpen] = useState<string | null>(null)
  return (
    <div>
      {rows.map((row, i) => {
        const visual = TIMELINE_ICON[row.kind]
        const canView = row.email && row.email !== latestEmail
        return (
          <div key={row.key} style={{ display: 'flex', gap: 12 }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: visual.fg,
                  background: visual.bg,
                  border: `1px solid ${visual.fg}40`,
                }}
              >
                {visual.icon}
              </span>
              {i < rows.length - 1 && (
                <span style={{ flex: 1, width: 1, background: p.gray[200] }} />
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingBottom: 14 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 7,
                  fontSize: 13,
                  fontWeight: 500,
                  color: p.gray[800],
                }}
              >
                {row.text}
                {i === 0 && (
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 600,
                      padding: '1px 7px',
                      borderRadius: 10,
                      background: p.gray[100],
                      color: p.gray[600],
                    }}
                  >
                    Latest
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11.5, color: p.gray[400], marginTop: 1 }}>
                {[fmtDateTime(row.at), row.sub].filter(Boolean).join(' · ')}
              </div>
              {canView && (
                <button
                  type="button"
                  aria-expanded={open === row.key}
                  onClick={() =>
                    setOpen((current) => (current === row.key ? null : row.key))
                  }
                  style={{
                    marginTop: 4,
                    padding: 0,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                    color: p.brand.primary,
                  }}
                >
                  {open === row.key ? 'Hide cart' : 'View cart'}
                </button>
              )}
              {canView && open === row.key && row.email && (
                <EmailCartAtSend email={row.email} />
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export const UpNextList: React.FC<{ emails: CartUpNext[] }> = ({ emails }) =>
  emails.length ? (
    <>
      <SectionTitle>Up next</SectionTitle>
      {emails.map((email) => (
        <div
          key={email.number}
          style={{ display: 'flex', gap: 12, paddingBottom: 10 }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: p.gray[400],
              border: `1px dashed ${p.gray[300]}`,
            }}
          >
            <Mail size={14} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, color: p.gray[600] }}>
              Email {email.number}: {email.subject}
            </div>
            <div style={{ fontSize: 11.5, color: p.gray[400] }}>
              {email.due_at
                ? `Sends ${fmtDateTime(email.due_at)} (in ${fmtIn(email.due_at)})`
                : `Sends ${fmtDelay(email.delay_value, email.delay_unit)} after the email before, if they haven't come back`}
            </div>
          </div>
        </div>
      ))}
    </>
  ) : null

// The API's timeline with each "sent" row tied to its email.
export const cartTimelineRows = (cart: CartDetail): TimelineRow[] =>
  [...cart.timeline]
    .sort(
      (a, b) => new Date(b.at ?? 0).getTime() - new Date(a.at ?? 0).getTime(),
    )
    .map((event, i) => ({
      key: `${event.type}-${event.email_number ?? ''}-${i}`,
      kind: event.type,
      at: event.at,
      text: event.text,
      sub: event.sub,
      email:
        event.type === 'sent'
          ? cart.emails.find((e) => e.number === event.email_number)
          : undefined,
    }))

// Footer buttons, offered by what the API says this cart allows.
export const CartActions: React.FC<{
  storeId: string
  cart: CartDetail
  showToast: (message: string, variant?: 'success' | 'error') => void
}> = ({ storeId, cart, showToast }) => {
  const { storeId: routeStoreId } = useParams<{ storeId: string }>()
  const navigate = useNavigate()
  const [confirming, setConfirming] = useState(false)
  const stop = useStopCartRecoveryCart(storeId)
  const sendNext = useSendNextCartRecoveryEmail(storeId)
  const busy = stop.isPending || sendNext.isPending
  const failed = (error: unknown) =>
    showToast(getMarketingErrorMessage(error), 'error')

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(cart.cart_link)
      showToast('Cart link copied')
    } catch {
      showToast("Couldn't copy the cart link", 'error')
    }
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {cart.customer.customer_id && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            navigate(customerPath(routeStoreId, cart.customer.customer_id))
          }
        >
          <User size={14} /> Customer profile
        </Button>
      )}
      {cart.order?.id && (
        <Button
          variant="primary"
          size="sm"
          onClick={() =>
            navigate(`/s/${routeStoreId}/orders/${cart.order?.id}/edit`)
          }
        >
          <ShoppingBag size={14} /> View order
          {cart.order.number ? ` #${cart.order.number}` : ''}
        </Button>
      )}
      {cart.actions.can_send_next && (
        <Button
          variant="ghost"
          size="sm"
          disabled={busy || Boolean(cart.send_pending)}
          onClick={() =>
            sendNext.mutate(cart.id, {
              onSuccess: ({ queued_email }) =>
                showToast(`Email ${queued_email} is on its way`),
              onError: failed,
            })
          }
        >
          <Send size={14} /> Send next email now
        </Button>
      )}
      {cart.actions.can_stop && (
        <Button
          variant="danger-ghost"
          size="sm"
          disabled={busy}
          onClick={() => setConfirming(true)}
        >
          <Square size={14} /> Stop reminders
        </Button>
      )}
      {cart.actions.can_copy_link && cart.cart_link && (
        <Button variant="ghost" size="sm" onClick={copyLink}>
          <LinkIcon size={14} /> Copy cart link
        </Button>
      )}
      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Stop reminders for this cart?"
        zIndex={1500}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={busy}
              onClick={() =>
                stop.mutate(cart.id, {
                  onSuccess: () => {
                    setConfirming(false)
                    showToast(`Reminders stopped for ${cart.display_id}`)
                  },
                  onError: failed,
                })
              }
            >
              Stop reminders
            </Button>
          </>
        }
      >
        <div style={{ fontSize: 13.5, color: p.gray[600] }}>
          No more recovery emails will be sent for {cart.display_id}. If they
          still check out, the order will count as recovered.
        </div>
      </Modal>
    </div>
  )
}
